import { useState, useEffect, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowLeft, Trophy, Clock, BookOpen, Calendar, Key, Award, Target, Zap, Crown, ChevronDown, ChevronUp } from 'lucide-react'
import NameEditor from '../components/NameEditor'
import { useGameSession } from '../context/GameSessionContext'
import { getMasterLeaderboard, MasterLeaderboardEntry } from '../utils/api'
import { categories as allCategories } from '../data/categories'
import {
  loginPlayer,
  saveProgress,
  getLeaderboard,
  type PlayerData,
  type LeaderboardEntry,
} from '../utils/api'
import { useGame } from '../context/GameContext'
import { checkNewAchievements, PlayerStats } from '../data/achievements'
import { vocabulary } from '../data/vocabulary'

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  const remainMins = mins % 60
  return `${hrs}h${remainMins > 0 ? `${remainMins}m` : ''}`
}

function buildCategoryWordCounts(wordIds: number[]): Record<string, number> {
  const counts: Record<string, number> = {}
  const wordMap = new Map(vocabulary.map(v => [v.id, v.category]))
  wordIds.forEach(id => {
    const cat = wordMap.get(id)
    if (cat) counts[cat] = (counts[cat] || 0) + 1
  })
  return counts
}

function formatDateTimeGMT7(isoString: string): string {
  const date = new Date(isoString)
  // Format in GMT+7 (Bangkok/Hanoi/Jakarta timezone)
  return date.toLocaleDateString('en-GB', { 
    timeZone: 'Asia/Bangkok',
    day: '2-digit', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function Leaderboard() {
  const location = useLocation()
  const { session, resetSession } = useGameSession()
  const { progress } = useGame()
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [showContinueForm, setShowContinueForm] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [error, setError] = useState('')
  const [currentPlayer, setCurrentPlayer] = useState<string | null>(null)
  const [currentPlayerData, setCurrentPlayerData] = useState<PlayerData | null>(null)
  const [activeTab, setActiveTab] = useState<'normal' | 'master'>('normal')
  const [masterEntries, setMasterEntries] = useState<MasterLeaderboardEntry[]>([])
  const [expandedMasterIds, setExpandedMasterIds] = useState<Set<number>>(new Set())
  const [expandedNormalIds, setExpandedNormalIds] = useState<Set<string>>(new Set())
  const [highlightMasterId, setHighlightMasterId] = useState<number | null>(null)
  const [sessionSaved, setSessionSaved] = useState(false)
  const [autoSaveAttempted, setAutoSaveAttempted] = useState(false)

  const loadLeaderboardData = useCallback(async () => {
    try {
      const list = await getLeaderboard()
      setEntries(list)
    } catch {
      // silently fail - getLeaderboard already falls back to localStorage
    }
  }, [])

  const loadMasterLeaderboardData = useCallback(async () => {
    try {
      const list = await getMasterLeaderboard()
      setMasterEntries(list)
    } catch {
      // silently fail
    }
  }, [])

  useEffect(() => {
    loadLeaderboardData()
    loadMasterLeaderboardData()
    const savedCurrent = localStorage.getItem('hsk2-current-player')
    if (savedCurrent) {
      setCurrentPlayer(savedCurrent)
      // Load current player data
      loginPlayer(savedCurrent).then(data => {
        setCurrentPlayerData(data)
      }).catch(() => {})
    }
  }, [loadLeaderboardData, loadMasterLeaderboardData])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    if (tab === 'master') setActiveTab('master')
    if (tab === 'normal') setActiveTab('normal')
  }, [location.search])

  useEffect(() => {
    if (activeTab !== 'master') return
    const timer = setTimeout(() => {
      loadMasterLeaderboardData()
      try {
        const raw = sessionStorage.getItem('master-highlight')
        if (!raw) return
        const hint = JSON.parse(raw) as { playerId?: string; totalScore?: number }
        sessionStorage.removeItem('master-highlight')
        const match = masterEntries.find(e => (
          (hint.playerId ? e.playerId === hint.playerId : true) &&
          (typeof hint.totalScore === 'number' ? e.totalScore === hint.totalScore : true)
        ))
        if (match) {
          setHighlightMasterId(match.id)
          setExpandedMasterIds(prev => new Set(prev).add(match.id))
        }
      } catch {
        // ignore
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [activeTab, loadMasterLeaderboardData, masterEntries])

  const toggleMasterExpand = (id: number) => {
    setExpandedMasterIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleNormalExpand = (id: string) => {
    setExpandedNormalIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const gameNameMap: Record<string, string> = {
    'picture-word': '🖼️ Picture Word',
    'audio-word': '🔊 Audio Word',
    'quiz': '🧠 Quiz',
    'matching': '🃏 Matching',
  }

  const continueWithPassword = async () => {
    setError('')
    const pw = passwordInput.trim()
    if (!pw) { setError('Please enter a password'); return }

    try {
      const data = await loginPlayer(pw)
      localStorage.setItem('hsk2-current-player', pw)
      setCurrentPlayer(pw)
      setCurrentPlayerData(data)
      setShowContinueForm(false)
      setPasswordInput('')
    } catch {
      setError('Password not found. Please check and try again.')
    }
  }

  const handleSaveScore = async () => {
    if (!currentPlayer || !currentPlayerData) return
    try {
      const wordsPlayed = progress.wordsLearned.map(wId => ({ wordId: wId, correct: true }))
      const existingWordIds = currentPlayerData.wordsLearned.map(w => w.wordId)
      const allWordIds = [...new Set([...existingWordIds, ...progress.wordsLearned])]
      const categoryWordsLearned = buildCategoryWordCounts(allWordIds)

      const stats: PlayerStats = {
        totalScore: currentPlayerData.totalScore + session.totalScore,
        bestStreak: Math.max(currentPlayerData.bestStreak, progress.maxStreak),
        wordsLearned: allWordIds.length,
        gamesPlayed: currentPlayerData.sessionsPlayed + 1,
        categoriesPlayed: [...new Set([...currentPlayerData.categoriesPlayed, ...session.selectedCategories])].length,
        perfectGames: currentPlayerData.perfectGames + (session.totalQuestions > 0 && session.correctAnswers === session.totalQuestions ? 1 : 0),
        totalCorrect: currentPlayerData.totalCorrect + session.correctAnswers,
        totalQuestions: currentPlayerData.totalQuestions + session.totalQuestions,
        categoryWordsLearned,
      }

      const newlyUnlocked = checkNewAchievements(stats, currentPlayerData.achievements)
      const now = new Date().toISOString()
      const newAchievementEntries = newlyUnlocked.map(a => ({ id: a.id, unlockedAt: now }))

      const updatedPlayer = await saveProgress({
        password: currentPlayer,
        name: currentPlayerData.name,
        sessionScore: session.totalScore,
        sessionCorrect: session.correctAnswers,
        sessionTotal: session.totalQuestions,
        sessionDuration: session.elapsedTime,
        gameMode: 'mixed',
        categories: session.selectedCategories,
        wordsPlayed,
        bestStreak: progress.maxStreak,
        isPerfect: session.totalQuestions > 0 && session.correctAnswers === session.totalQuestions,
        achievements: newAchievementEntries,
      })

      setCurrentPlayerData(updatedPlayer)
      setSessionSaved(true)
      resetSession()
      await loadLeaderboardData()
    } catch (e) {
      console.error('Failed to save score:', e)
    }
  }

  useEffect(() => {
    if (autoSaveAttempted) return
    if (!currentPlayer || !currentPlayerData) return
    if (session.totalScore <= 0 || sessionSaved) return

    setAutoSaveAttempted(true)
    handleSaveScore()
  }, [autoSaveAttempted, currentPlayer, currentPlayerData, session.totalScore, sessionSaved])

  return (
    <div className="page-shell bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 p-3 md:p-4 flex flex-col">
      <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 md:gap-4 mb-2">
          <Link
            to="/"
            className="bg-white/20 rounded-2xl p-2.5 md:p-3 active:scale-95 transition-transform"
          >
            <ArrowLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </Link>

          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
            <Trophy className="w-8 h-8 md:w-10 md:h-10 text-yellow-300 shrink-0" />
            <h1 className="text-2xl md:text-3xl font-bold text-white truncate">Leaderboard</h1>
          </div>
        </div>

        {/* Tab Switcher - at the top */}
        <div className="bg-white/10 backdrop-blur rounded-2xl p-1.5 mb-3 flex items-center gap-2">
          <button
            onClick={() => setActiveTab('normal')}
            className={`flex-1 rounded-xl py-2 px-3 text-sm font-bold transition-all flex items-center justify-center gap-1.5
              ${activeTab === 'normal' ? 'bg-white text-orange-700 shadow-lg' : 'text-white/60'}`}
          >
            <Trophy className="w-4 h-4" /> Normal
          </button>
          <button
            onClick={() => setActiveTab('master')}
            className={`flex-1 rounded-xl py-2 px-3 text-sm font-bold transition-all flex items-center justify-center gap-1.5
              ${activeTab === 'master' ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-900 shadow-lg' : 'text-white/60'}`}
          >
            <Crown className="w-4 h-4" /> Master Mode
          </button>
        </div>

        {/* Continue with Password Modal */}
        {showContinueForm && (
          <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-4 mb-4 shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <Key className="w-5 h-5 text-yellow-300" />
              <h2 className="text-lg font-bold text-white">Continue with Password</h2>
            </div>
            <p className="text-white/80 text-sm mb-3">Enter your 8-character password to restore your progress</p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <input
                type="text"
                placeholder="Enter password..."
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value.slice(0, 8))}
                className="flex-1 rounded-2xl px-4 py-3 text-lg bg-white/90 text-gray-800
                           placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400
                           font-mono tracking-wider"
                maxLength={8}
              />
              <button
                onClick={continueWithPassword}
                className="bg-green-500 text-white rounded-2xl px-4 sm:px-6 py-3 font-bold
                           active:scale-95 transition-transform shadow-xl"
              >
                Restore
              </button>
              <button
                onClick={() => { setShowContinueForm(false); setError('') }}
                className="bg-white/20 text-white rounded-2xl px-4 py-3 font-bold
                           active:scale-95 transition-transform"
              >
                Cancel
              </button>
            </div>
            {error && <p className="text-red-200 text-sm mt-2">{error}</p>}
          </div>
        )}

        {/* Current Player Info */}
        {currentPlayerData && (
          <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-4 mb-4 shadow-xl">
            <div className="flex items-center justify-between flex-wrap gap-2 md:gap-3">
              <div className="min-w-0">
                <p className="text-white/70 text-xs">Current Player</p>
                <NameEditor
                  currentName={currentPlayerData.name}
                  password={currentPlayer || ''}
                  onRenamed={(newName) => {
                    setCurrentPlayerData(prev => prev ? { ...prev, name: newName } : prev)
                    localStorage.setItem('hsk2-player-name', newName)
                    loadLeaderboardData()
                    loadMasterLeaderboardData()
                  }}
                  textClassName="text-xl md:text-2xl font-bold text-white"
                />
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-yellow-300">{currentPlayerData.totalScore}</p>
                <p className="text-white/70 text-xs">Total Points</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-white">{currentPlayerData.sessionsPlayed}</p>
                <p className="text-white/70 text-xs">Sessions</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-white">{currentPlayerData.achievements.length}</p>
                <p className="text-white/70 text-xs">Achievements</p>
              </div>
            </div>
          </div>
        )}

        {/* Session Saved Message */}
        {sessionSaved && (
          <div className="bg-green-500/30 backdrop-blur-lg rounded-3xl p-4 mb-4 shadow-xl border-2 border-green-400">
            <p className="text-green-200 font-bold text-center">✅ Session saved to your total score!</p>
          </div>
        )}

        {/* Normal Leaderboard List */}
        {activeTab === 'normal' && (
          <div className="flex-1 bg-white/20 backdrop-blur-lg rounded-3xl p-4 shadow-xl overflow-auto">
            {entries.length === 0 ? (
              <div className="text-center text-white/70 py-10">
                <Trophy className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-xl">No scores yet!</p>
                <p className="text-sm">Be the first to play and save your score.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {entries.map((entry, index) => {
                  const isCurrentPlayer = currentPlayer === entry.password
                  const entryAccuracy = entry.totalQuestions > 0 ? Math.round((entry.totalCorrect / entry.totalQuestions) * 100) : 0
                  const avgScorePerGame = entry.sessionsPlayed > 0 ? Math.round(entry.totalScore / entry.sessionsPlayed) : 0
                  const isExpanded = expandedNormalIds.has(entry.password)
                  return (
                    <div
                      key={entry.password}
                      className={`rounded-2xl p-3 ${
                        isCurrentPlayer ? 'bg-green-400/30 ring-2 ring-green-400' :
                        index === 0 ? 'bg-yellow-400/30' :
                        index === 1 ? 'bg-gray-300/30' :
                        index === 2 ? 'bg-amber-600/30' :
                        'bg-white/10'
                      }`}
                    >
                      <button onClick={() => toggleNormalExpand(entry.password)} className="w-full text-left">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0
                            ${index === 0 ? 'text-yellow-300' : index === 1 ? 'text-gray-300' : index === 2 ? 'text-amber-600' : 'text-white/70'}`}>
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-white text-lg truncate">
                              {entry.name}
                              {isCurrentPlayer && <span className="text-green-300 text-sm ml-2">(You)</span>}
                            </p>
                            <div className="flex items-center gap-2 text-white/60 text-[11px] flex-wrap">
                              <span className="flex items-center gap-0.5">
                                <Zap className="w-3 h-3" />
                                🔥{entry.bestStreak}
                              </span>
                              <span className="flex items-center gap-0.5">
                                <Clock className="w-3 h-3" />
                                {formatDuration(entry.totalDuration)}
                              </span>
                              <span className="flex items-center gap-0.5">
                                <BookOpen className="w-3 h-3" />
                                {entry.wordsLearned} unique words
                              </span>
                              <span className="flex items-center gap-0.5">
                                <Target className="w-3 h-3" />
                                {entryAccuracy}% acc
                              </span>
                              <span className="flex items-center gap-0.5">
                                <Award className="w-3 h-3" />
                                {entry.achievementsCount}🏅
                              </span>
                              <span className="flex items-center gap-0.5">
                                <Calendar className="w-3 h-3" />
                                {entry.sessionsPlayed} sessions
                              </span>
                            </div>
                          </div>

                          <div className="text-right flex-shrink-0 flex items-center gap-2">
                            <div>
                              <p className="font-bold text-2xl text-white">{entry.totalScore}</p>
                              <p className="text-white/60 text-[10px]">total pts</p>
                            </div>
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
                          </div>
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="mt-3 bg-black/20 rounded-xl p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                          <div>
                            <p className="text-lg font-bold text-yellow-300">{avgScorePerGame}</p>
                            <p className="text-white/60 text-[10px]">Avg / Session</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-green-300">{entry.totalCorrect}/{entry.totalQuestions}</p>
                            <p className="text-white/60 text-[10px]">Correct / Total</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-pink-300">{entry.perfectGames}</p>
                            <p className="text-white/60 text-[10px]">Perfect Games</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-blue-300">{formatDateTimeGMT7(entry.lastPlayedAt)}</p>
                            <p className="text-white/60 text-[10px]">Last Played</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Master Mode Leaderboard */}
        {activeTab === 'master' && (
          <div className="flex-1 bg-white/20 backdrop-blur-lg rounded-3xl p-4 shadow-xl overflow-auto">
            {masterEntries.length === 0 ? (
              <div className="text-center text-white/70 py-10">
                <Crown className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-xl">No Master Mode records yet!</p>
                <p className="text-sm">Complete a Master Mode session to see your stats here.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {masterEntries.map((entry, index) => {
                  const isExpanded = expandedMasterIds.has(entry.id)
                  const isHighlighted = highlightMasterId === entry.id
                  return (
                    <div key={entry.id} className={`rounded-2xl overflow-hidden ${
                      isHighlighted
                        ? 'bg-green-500/20 ring-2 ring-green-300/70'
                        : entry.isComplete ? 'bg-yellow-400/20 ring-1 ring-yellow-400/40' : 'bg-white/10'
                    }`}>
                      {/* Summary row */}
                      <button
                        onClick={() => toggleMasterExpand(entry.id)}
                        className="w-full p-3 flex items-center gap-3 text-left"
                      >
                        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                          {entry.isComplete ? '👑' : `#${index + 1}`}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-white text-lg truncate">{entry.playerName}</p>
                            {entry.isComplete && (
                              <span className="bg-yellow-400 text-yellow-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full">COMPLETE</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-white/60 text-[11px] flex-wrap">
                            <span>🎮 {entry.gamesCompleted}/4 games</span>
                            <span>📚 {entry.totalWords} words</span>
                            <span>🎯 {entry.overallAccuracy}%</span>
                            <span>🔥 {entry.maxStreak} streak</span>
                            <span><Clock className="w-3 h-3 inline" /> {formatDuration(entry.totalTime)}</span>
                            <span><Calendar className="w-3 h-3 inline" /> {formatDateTimeGMT7(entry.date)}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 flex items-center gap-2">
                          <div>
                            <p className="font-bold text-2xl text-yellow-300">{entry.totalScore}</p>
                            <p className="text-white/60 text-[10px]">pts</p>
                          </div>
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
                        </div>
                      </button>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className="px-3 pb-3 space-y-2">
                          {/* Categories */}
                          <div className="bg-black/20 rounded-xl p-2">
                            <p className="text-white/50 text-[10px] mb-1">Categories</p>
                            <div className="flex flex-wrap gap-1">
                              {entry.categories.map(cat => {
                                const catInfo = allCategories.find(c => c.id === cat)
                                return catInfo ? (
                                  <span key={cat} className="bg-white/15 rounded-lg px-2 py-0.5 text-white text-xs">
                                    {catInfo.icon} {catInfo.nameEn}
                                  </span>
                                ) : null
                              })}
                            </div>
                          </div>
                          {/* Per-game stats */}
                          <div className="grid grid-cols-2 gap-2">
                            {['picture-word', 'audio-word', 'quiz', 'matching'].map(gameId => {
                              const stat = entry.gameStats[gameId]
                              const gameName = gameNameMap[gameId] || gameId
                              return (
                                <div key={gameId} className={`rounded-xl p-2 ${stat ? 'bg-green-500/20' : 'bg-white/5'}`}>
                                  <p className="text-white font-bold text-xs mb-1">{gameName}</p>
                                  {stat ? (
                                    <div className="text-white/70 text-[10px] space-y-0.5">
                                      <p>✅ {stat.correct} correct / {stat.total} total</p>
                                      <p>🎯 {stat.total > 0 ? Math.round((stat.correct / stat.total) * 100) : 0}% accuracy</p>
                                      <p>🔥 {stat.maxStreak} max streak</p>
                                      <p>⭐ {stat.score} score</p>
                                    </div>
                                  ) : (
                                    <p className="text-white/30 text-[10px]">Not played</p>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                          {/* Summary stats */}
                          <div className="bg-black/20 rounded-xl p-2 grid grid-cols-4 gap-2 text-center">
                            <div>
                              <p className="text-lg font-bold text-yellow-300">{entry.totalScore}</p>
                              <p className="text-white/50 text-[9px]">Total Score</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-green-300">{entry.overallAccuracy}%</p>
                              <p className="text-white/50 text-[9px]">Accuracy</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-blue-300">{formatDuration(entry.totalTime)}</p>
                              <p className="text-white/50 text-[9px]">Duration</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-pink-300">{entry.maxStreak}</p>
                              <p className="text-white/50 text-[9px]">Max Streak</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
